import {AnimatePresence, motion, MotionStyle} from 'framer-motion';
import {
  getColorByPath,
  getSize,
  Integer,
  ThemeColorPath,
  ThemeRadiusPath,
} from 'platform/foundation';
import styled, {css, keyframes} from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda-adjunct';

export interface SkeletonBoxProps {
  width?: Integer;
  height?: Integer;
  borderRadius?: ThemeRadiusPath;
  backgroundColor?: ThemeColorPath;
  isLoading?: boolean;
  children?: ReactNode;
  contentStyles?: MotionStyle;
}

const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 0.6;
  }
`;

const StyledSkeletonBox = styled.div<Omit<SkeletonBoxProps, 'children'>>`
  ${(props) => css`
    width: ${isNotNil(props.width) ? getSize(props.width) : '100%'};
    height: ${isNotNil(props.height) ? getSize(props.height) : '100%'};
    border-radius: ${({theme}) =>
      isNotNil(props.borderRadius) ? theme.radii[props.borderRadius] : '4px'};
    background-color: ${({theme}) =>
      getColorByPath(props.backgroundColor, theme) ?? theme.colors.palettes.neutral[50][100]};
    animation: ${pulse} 1.5s ease-in-out infinite;
  `}
`;

export const SkeletonLoadingBox = (props: SkeletonBoxProps) => {
  const {children, isLoading, contentStyles, ...rest} = props;

  return (
    <AnimatePresence initial={false} mode="wait">
      {isLoading ? (
        <motion.div
          key="skeleton"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.2}}
        >
          <StyledSkeletonBox {...rest} />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{opacity: 1}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.2}}
          style={{
            height: 'inherit',
            width: 'inherit',
            ...contentStyles,
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
